/**
 * WarmHandoffsLanding
 * --------------------
 * Public-facing explainer page for the "Warm Handoffs" partner program.
 * Serves 3 audiences:
 *   1. Existing partners who got the announce email and want the details.
 *   2. Prospective partners considering applying.
 *   3. Ken Burks (and the like) who just got a text pointing them here.
 */
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Users2 as Handshake, DollarSign, Shield, Zap, CheckCircle2,
  MessageSquare, Mail, ArrowRight, Sparkles, Clock, TrendingUp,
  Target,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// YouTube video ID for the 60-sec founder walkthrough.
// Source: https://youtube.com/shorts/ngKSjf2MCn8
// Shorts are portrait (9:16) — container below renders in portrait aspect.
const WARM_HANDOFFS_VIDEO_ID = 'ngKSjf2MCn8';
const WARM_HANDOFFS_VIDEO_IS_SHORT = true;

// ── SEO configuration ─────────────────────────────────────────
// Tuned for longtail referral-program + voice-search intent:
//  "how does warm intro referral program work",
//  "best partner program that pays commission instantly",
//  "alternative to partner tracking links",
//  "referral program without cookies"
const SEO_TITLE = 'Warm Handoffs — Partner Program That Pays You Instantly (No Tracking Links) | Local Branding Pro';
const SEO_DESCRIPTION =
  'The Warm Handoff is a better way to refer businesses to Local Branding Pro. Submit a warm intro in 30 seconds, consent-first, and your commission locks in the moment you hit submit — no cookies, no tracking-link clicks, no hoping. Email + SMS outreach handled for you.';
const SEO_KEYWORDS = [
  // Primary
  'warm handoff partner program',
  'partner referral program that pays commission instantly',
  'alternative to partner tracking links',
  'referral program without cookies',
  'consent-first referral program',
  // Voice-search conversational longtail
  'how do I refer a business to a digital marketing agency',
  'what is a warm handoff in sales',
  'best way to refer a client and still get paid',
  'how to earn commission without tracking links',
  'can I refer someone by text and still get credit',
  // Service category
  'digital marketing partner program',
  'SEO referral program',
  'web design referral program',
  'TCPA-clean referral program',
  'partner HQ local-branding-pro',
].join(', ');
const SEO_CANONICAL = 'https://localbrandingpro.com/warm-handoffs';
// OG image — reuse existing brand asset; swap for a dedicated Warm Handoffs
// share card when one is designed.
const SEO_OG_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/3ffa77a1-0bda-46eb-9179-0a59cf5bf2e9/images/6a98610b6231c606fb2427cf455bc688e724a3a8c86b57edf4b5d30439b13924.png';

// Schema stacking — five entities wrapped in a @graph so Google processes
// each as a first-class node: WebPage, Service, Organization, VideoObject, FAQPage.
const SEO_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${SEO_CANONICAL}#webpage`,
      url: SEO_CANONICAL,
      name: SEO_TITLE,
      description: SEO_DESCRIPTION,
      inLanguage: 'en-US',
      isPartOf: { '@id': 'https://localbrandingpro.com/#website' },
      primaryImageOfPage: SEO_OG_IMAGE,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://localbrandingpro.com/' },
          { '@type': 'ListItem', position: 2, name: 'Partner Program', item: 'https://localbrandingpro.com/partners' },
          { '@type': 'ListItem', position: 3, name: 'Warm Handoffs', item: SEO_CANONICAL },
        ],
      },
    },
    {
      '@type': 'Service',
      '@id': `${SEO_CANONICAL}#service`,
      name: 'Warm Handoffs Partner Referral Program',
      serviceType: 'Partner Referral Program',
      provider: { '@id': 'https://localbrandingpro.com/#organization' },
      areaServed: { '@type': 'Country', name: 'United States' },
      description:
        'A consent-first partner referral program where warm intros submitted through Partner HQ automatically lock in commission attribution the moment of submission — no tracking links required.',
      offers: {
        '@type': 'Offer',
        description: 'Commission attribution on close. Email + SMS outreach handled by Local Branding Pro on the partner\'s behalf.',
        availability: 'https://schema.org/InStock',
      },
    },
    {
      '@type': 'Organization',
      '@id': 'https://localbrandingpro.com/#organization',
      name: 'Local Branding Pro',
      url: 'https://localbrandingpro.com',
      logo: 'https://localbrandingpro.com/logo.png',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+1-817-264-7476',
        contactType: 'customer service',
        areaServed: 'US',
        availableLanguage: 'English',
      },
    },
    {
      '@type': 'VideoObject',
      '@id': `${SEO_CANONICAL}#video`,
      name: 'Warm Handoffs — 60-second founder walkthrough',
      description:
        'Larry Scala explains why Local Branding Pro built the Warm Handoff partner program: the easiest way to hand a lead to us and still get paid, without forcing your friends to click tracked links.',
      thumbnailUrl: `https://img.youtube.com/vi/${WARM_HANDOFFS_VIDEO_ID}/hqdefault.jpg`,
      uploadDate: '2026-04-18',
      contentUrl: `https://www.youtube.com/watch?v=${WARM_HANDOFFS_VIDEO_ID}`,
      embedUrl: `https://www.youtube.com/embed/${WARM_HANDOFFS_VIDEO_ID}`,
    },
    {
      '@type': 'FAQPage',
      '@id': `${SEO_CANONICAL}#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is a warm handoff in sales and referral marketing?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A warm handoff is when someone personally introduces a prospect to a vendor, rather than handing over a tracked partner link. It comes from sales — the top reps always introduce prospects personally. Local Branding Pro built a partner program around it so real-world introductions get paid, not just link-clicks.',
          },
        },
        {
          '@type': 'Question',
          name: 'How do I refer a business without using a tracking link?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Log into Partner HQ, click Warm Intro, and submit the prospect\'s name and email or phone along with a short note. Your commission locks in on submission. Local Branding Pro handles the email or SMS outreach with your name and consent already cited. No cookies, no clicks, no hoping the link is honored.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I refer someone by text message and still get credit?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. The Warm Handoff form lets you submit a prospect you spoke with by text, phone, coffee, or Zoom. Because attribution happens at submit — not at click — text-message referrals get paid the same as link referrals.',
          },
        },
        {
          '@type': 'Question',
          name: 'Is the Warm Handoff program TCPA and CAN-SPAM compliant?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. The form requires two explicit partner certifications before submission: (1) the prospect gave verbal or written permission to be contacted, and (2) the partner agrees to be named as the referrer. Every outbound message cites the referring partner by name and offers a one-click remove.',
          },
        },
        {
          '@type': 'Question',
          name: 'How fast does commission attribution happen?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Immediately. The moment you hit submit, the prospect is written into our contacts database with your partner code attached. When they convert — days, weeks, or months later — the commission credits automatically on Stripe checkout.',
          },
        },
      ],
    },
  ],
};

export default function WarmHandoffsLanding() {
  const { t } = useTranslation('partner');
  const trackedRef = useRef(false);
  // Fire a single lightweight attribution ping when a partner-shared link
  // (`?ref=<code>&src=<channel>`) is detected. No-op otherwise.
  useEffect(() => {
    if (trackedRef.current) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = (params.get('ref') || '').trim();
      if (!ref) return;
      const src = (params.get('src') || 'other').trim().toLowerCase();
      trackedRef.current = true;
      const payload = { ref, src, path: '/warm-handoffs' };
      const url = `${API}/api/warm-handoffs/track-landing`;
      // Prefer sendBeacon so the ping survives quick bounces.
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      } else {
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {});
      }
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { document.title = SEO_TITLE; }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50" data-testid="warm-handoffs-landing">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 pointer-events-none opacity-40" aria-hidden>
          <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-teal-200 blur-3xl" />
          <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-teal-100 blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 py-16 sm:py-24">
          <Badge className="bg-amber-100 text-amber-900 border-amber-200 text-xs font-semibold tracking-wide px-3 py-1 mb-5">
            <Sparkles className="h-3.5 w-3.5 mr-1.5 inline" />
            {t('hero.badge')}
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-[1.05]">
            {t('hero.title1')}
            <br />
            <span className="text-teal-600">{t('hero.title2')}</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
            {t('hero.subtitle')}{' '}
            <strong className="text-slate-900">{t('hero.subtitleStrong')}</strong>{' '}
            {t('hero.subtitleEnd')}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/partner/hq">
              <Button className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-7 py-6 text-base rounded-lg shadow-sm" data-testid="wh-cta-partner-hq">
                {t('hero.ctaPrimary')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/affiliate/signup">
              <Button variant="outline" className="border-slate-300 text-slate-700 px-7 py-6 text-base rounded-lg" data-testid="wh-cta-apply">
                {t('hero.ctaSecondary')}
              </Button>
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-600">
            {(t('hero.stats', { returnObjects: true }) || []).map((label, i) => (
              <Stat key={i} icon={[Zap, Clock, Mail, Shield][i]} label={label} />
            ))}
          </div>
        </div>
      </section>

      {/* Video — 60-sec founder walkthrough (placeholder until recorded) */}
      <section className="bg-white border-b border-slate-200" data-testid="wh-video-section">
        <div className="max-w-4xl mx-auto px-6 py-14 sm:py-16">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <p className="text-xs font-semibold tracking-wider text-teal-600 uppercase mb-3">{t('video.eyebrow')}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
              {t('video.title')}
            </h2>
            <p className="mt-3 text-slate-600 leading-relaxed">
              {t('video.subtitle')}
            </p>
          </div>
          <div
            className={`relative ${WARM_HANDOFFS_VIDEO_IS_SHORT ? 'aspect-[9/16] max-w-xs mx-auto' : 'aspect-video'} rounded-xl overflow-hidden border border-slate-200 shadow-sm bg-slate-900`}
            data-testid="wh-video-embed"
          >
            {WARM_HANDOFFS_VIDEO_ID ? (
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${WARM_HANDOFFS_VIDEO_ID}?rel=0&modestbranding=1&playsinline=1`}
                title="Warm Handoffs — 60-second walkthrough"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 bg-gradient-to-br from-slate-800 via-slate-900 to-blue-950">
                <div className="h-16 w-16 rounded-full bg-white/10 border border-white/20 flex items-center justify-center mb-4 backdrop-blur-sm">
                  <div className="h-0 w-0 border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent border-l-[16px] border-l-white ml-1" aria-hidden />
                </div>
                <p className="text-white font-semibold text-lg">{t('video.comingSoon')}</p>
                <p className="mt-1.5 text-slate-300 text-sm max-w-sm">
                  {t('video.comingSoonSub')}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Why it exists */}
      <section className="max-w-5xl mx-auto px-6 py-16 sm:py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-semibold tracking-wider text-teal-600 uppercase mb-3">{t('problem.eyebrow')}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
              {t('problem.title')}
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed text-lg">
              {t('problem.p1Pre')}
              <code className="mx-1.5 px-1.5 py-0.5 rounded bg-slate-100 text-slate-800 text-sm">{t('problem.p1RefCode')}</code>
              {t('problem.p1Post')}
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed text-lg">
              {t('problem.p2')}
            </p>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-teal-100 rounded-2xl blur-2xl opacity-50" aria-hidden />
            <div className="relative bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
              <div className="flex items-start gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-teal-600 text-white flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{t('problem.cardTitle')}</p>
                  <p className="text-xs text-slate-500">{t('problem.cardSub')}</p>
                </div>
              </div>
              <div className="space-y-2.5 text-sm">
                <div className="ml-auto max-w-[85%] bg-teal-600 text-white rounded-2xl rounded-br-md px-4 py-2.5">
                  {t('problem.msg1')}
                </div>
                <div className="ml-auto max-w-[85%] bg-teal-600 text-white rounded-2xl rounded-br-md px-4 py-2.5">
                  {t('problem.msg2')}
                </div>
                <div className="max-w-[85%] bg-slate-100 text-slate-700 rounded-2xl rounded-bl-md px-4 py-2.5 italic">
                  {t('problem.msgReply')}
                </div>
              </div>
              <div className="mt-5 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800 flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{t('problem.answer')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-xs font-semibold tracking-wider text-teal-600 uppercase mb-3">{t('how.eyebrow')}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">{t('how.title')}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {(t('how.steps', { returnObjects: true }) || []).map((s, i) => (
              <div key={i} className="relative bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-lg bg-teal-600 text-white flex items-center justify-center flex-shrink-0 font-bold">
                    {i + 1}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-slate-900">{s.title}</h3>
                    <p className="mt-1.5 text-slate-600 leading-relaxed">{s.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What you earn */}
      <section className="max-w-5xl mx-auto px-6 py-16 sm:py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {(t('earnings', { returnObjects: true }) || []).map((e, i) => {
            const Icon = [DollarSign, TrendingUp, Target][i] || DollarSign;
            return (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 hover:border-blue-300 transition">
                <div className="h-10 w-10 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">{e.title}</h3>
                <p className="mt-1.5 text-slate-600 text-sm leading-relaxed">{e.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Live-dashboard teaser */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-16 sm:py-20">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-xs font-semibold tracking-wider text-blue-400 uppercase mb-3">{t('live.eyebrow')}</p>
              <h2 className="text-3xl sm:text-4xl font-bold leading-tight">
                {t('live.title')}
              </h2>
              <p className="mt-5 text-slate-300 leading-relaxed text-lg">
                {t('live.subtitle')}
              </p>
              <ul className="mt-6 space-y-2.5 text-slate-200">
                {(t('live.bullets', { returnObjects: true }) || []).map((tx, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <CheckCircle2 className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span>{tx}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 shadow-2xl">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{t('live.panelTitle')}</p>
                <Badge className="bg-teal-500/20 text-blue-300 border-teal-500/30 text-[10px]">{t('live.live')}</Badge>
              </div>
              {(t('live.rows', { returnObjects: true }) || []).map((r, i) => {
                const meta = SAMPLE_ROW_META[i] || SAMPLE_ROW_META[0];
                return (
                  <div key={i} className="py-3 border-b border-slate-700 last:border-0">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-white text-sm">{r.name}</span>
                      <Badge className={`text-[10px] h-5 ${meta.badgeCls}`}>{r.status}</Badge>
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-xs text-slate-400">
                      {(r.chips || []).map((label, j) => (
                        <span key={j} className={meta.chipCls[j]}>{meta.chipIcons[j]} {label}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-16 sm:py-20">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-xs font-semibold tracking-wider text-teal-600 uppercase mb-3">{t('faqHeading.eyebrow')}</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">{t('faqHeading.title')}</h2>
        </div>
        <div className="space-y-3">
          {(t('faqs', { returnObjects: true }) || []).map((f, i) => (
            <details key={i} className="group bg-white border border-slate-200 rounded-lg">
              <summary className="cursor-pointer px-5 py-4 font-semibold text-slate-900 hover:bg-slate-50 flex items-center justify-between">
                <span>{f.q}</span>
                <span className="text-slate-400 group-open:rotate-45 transition">+</span>
              </summary>
              <div className="px-5 pb-4 text-slate-600 leading-relaxed text-[15px]">{f.a}</div>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto px-6 py-16 sm:py-20 text-center">
          <Handshake className="h-12 w-12 mx-auto mb-5 opacity-90" />
          <h2 className="text-3xl sm:text-4xl font-bold">{t('finalCta.title')}</h2>
          <p className="mt-4 text-lg text-blue-100 leading-relaxed max-w-xl mx-auto">
            {t('finalCta.subtitle')}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/partner/hq">
              <Button className="bg-white text-teal-700 hover:bg-teal-50 font-semibold px-8 py-6 text-base rounded-lg shadow-sm" data-testid="wh-cta-footer">
                {t('finalCta.primary')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-white/40 bg-transparent text-white hover:bg-white/10 px-8 py-6 text-base rounded-lg">
                {t('finalCta.secondary')}
              </Button>
            </Link>
          </div>
          <p className="mt-8 text-xs text-blue-100/80">
            {t('finalCta.applyPre')} <Link to="/affiliate/signup" className="underline hover:text-white">{t('finalCta.applyLink')}</Link>
          </p>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-2">
      <Icon className="h-4 w-4 text-teal-600" />
      <span>{label}</span>
    </span>
  );
}

// Visual styling metadata for sample dashboard rows (kept here so we don't
// stuff CSS classes into the locale JSON). Index-aligned with t('live.rows').
const SAMPLE_ROW_META = [
  {
    badgeCls: "bg-amber-500/20 text-amber-300 border-amber-500/30",
    chipIcons: ["✉️", "👁", "🗓"],
    chipCls: ["text-emerald-400", "text-blue-300", "text-emerald-400 font-medium"],
  },
  {
    badgeCls: "bg-teal-500/20 text-blue-300 border-teal-500/30",
    chipIcons: ["📱", "📖"],
    chipCls: ["text-emerald-400", "text-blue-300 font-medium"],
  },
  {
    badgeCls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
    chipIcons: ["💰"],
    chipCls: ["text-emerald-300 font-medium"],
  },
];
